/**
 * **How a member reads an event's date, and an organizer's initials** — one implementation, shared.
 *
 * ## Why these moved out of `apps/web`
 *
 * `EVT-010` AC-4, ruled 2026-08-30: the wizard's Review step says *"This is exactly what a member
 * sees"* and must render the member view **from the member surface's own component**. That
 * component is {@link MemberEventView}, and it lives here because `apps/web` and `apps/organizer`
 * are different applications — the same argument `OrganizerProfileCard` made for `ORG-017` AC-4.
 *
 * These three functions came with it. They were `apps/web`'s, and a copy in the shared component
 * would have been the *date line* — the most-read line on the page — rendered by two functions
 * that agree until one is edited. `apps/web` re-exports them from its own `constants.ts`, so every
 * call site there is unchanged and there is still exactly one definition.
 *
 * This is the same reading `@h2hspark/money` was created under and `AGENTS.md` convention 4 now
 * records: **how a thing is written is presentation, not business logic.** What an event *is*
 * stays in the events domain; nothing here reads a store, decides a state, or knows a lifecycle.
 */
import { organizationInitials } from './initials';
/**
 * `MEM-015` AC-2: **the event's timezone, never the reader's device timezone**, with the
 * abbreviation shown where it differs from the reader's.
 *
 * ## Why the abbreviation is compared and not the zone name
 *
 * The AC says "where it differs from the reader's". Comparing IANA identifiers would append
 * "EDT" for a reader in Toronto looking at an event in New York — two different names, the same
 * clock, and a label that tells them nothing. Comparing the **resolved abbreviation at that
 * instant** is the same test asked about the thing a reader actually experiences, and it keeps
 * the common case clean, which the story's note gives as the point.
 *
 * ## Why there is no fallback to the reader's zone
 *
 * An event with no stored timezone is rendered in UTC and labelled UTC. Falling back to the
 * reader's would produce a plausible, confident, wrong hour — which is precisely the failure the
 * AC is written against ("a member in another zone who sees their own local time will arrive at
 * the wrong hour"). A visible `UTC` is a member asking the organizer; a silent local time is a
 * member arriving at the wrong hour.
 *
 * **A formatted date is never asserted against a literal** — `docs/standards/time-and-timezone.md`,
 * and the exception `@h2hspark/money` earned does not extend here: `Intl.DateTimeFormat` is
 * genuinely doing localisation and there is no hand-rolled substitute for a time zone.
 */
export function formatEventDateTime(input) {
    const start = new Date(input.startsAt);
    if (Number.isNaN(start.getTime()))
        return '';
    const zone = input.timezone ?? 'UTC';
    const style = input.style ?? 'short';
    const date = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        weekday: style === 'long' ? 'long' : 'short',
        month: style === 'long' ? 'long' : 'short',
        day: 'numeric',
    }).format(start);
    const time = formatTime(start, zone);
    const end = input.endsAt === undefined || input.endsAt === null ? null : new Date(input.endsAt);
    const window = end !== null && !Number.isNaN(end.getTime()) && style === 'long'
        ? `${time} – ${formatTime(end, zone)}`
        : time;
    const abbreviation = timezoneAbbreviation(start, zone);
    const readerAbbreviation = timezoneAbbreviation(start, input.reader);
    const suffix = abbreviation !== null && abbreviation !== readerAbbreviation ? ` ${abbreviation}` : '';
    return `${date} · ${window}${suffix}`;
}
/**
 * **The event's day alone** — `Thu, Oct 8` — in the event's own timezone (`MEM-015` AC-2).
 *
 * For a place that states the hours on a line of their own beside it: the checkout's summary
 * card, which carries the date as its eyebrow and the start and end beneath the name (TW
 * 44132872). `''` for an instant that does not parse, the rule {@link formatEventDateTime} keeps.
 */
export function formatEventDate(input) {
    const start = new Date(input.startsAt);
    if (Number.isNaN(start.getTime()))
        return '';
    return new Intl.DateTimeFormat('en-US', {
        timeZone: input.timezone ?? 'UTC',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }).format(start);
}
/**
 * **When the event starts and ends** — `6:00 PM – 9:00 PM`, with the zone's abbreviation where it
 * differs from the reader's (`MEM-015` AC-2), and the start alone for an event with no end.
 *
 * The hours half of {@link formatEventDateTime}'s long form, for the line that sits under a date
 * already stated. The same `formatTime`, so the two cannot write an hour differently.
 */
export function formatEventTimeWindow(input) {
    const start = new Date(input.startsAt);
    if (Number.isNaN(start.getTime()))
        return '';
    const zone = input.timezone ?? 'UTC';
    const end = input.endsAt === undefined || input.endsAt === null ? null : new Date(input.endsAt);
    const window = end !== null && !Number.isNaN(end.getTime())
        ? `${formatTime(start, zone)} – ${formatTime(end, zone)}`
        : formatTime(start, zone);
    const abbreviation = timezoneAbbreviation(start, zone);
    const readerAbbreviation = timezoneAbbreviation(start, input.reader);
    const suffix = abbreviation !== null && abbreviation !== readerAbbreviation ? ` ${abbreviation}` : '';
    return `${window}${suffix}`;
}
function formatTime(instant, zone) {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hour: 'numeric',
        minute: '2-digit',
    }).format(instant);
}
/**
 * **The date tile's two lines** — `Sep` over `23` — in the event's own timezone
 * (`MEM-015` AC-2's rule, which a tile is just as able to break as a sentence).
 *
 * `Web App.dc.html` (10 September) draws a date tile on every event it lists: the Discover card,
 * the Your Events rows and the Next up card among them. The tile is a month and a day and
 * nothing else, so the zone that decides them is the only thing worth getting right: 11 PM on
 * the 23rd in Providence is the 24th in UTC, and a tile read off the wrong clock tells a member
 * the wrong night.
 *
 * `null` for an instant that does not parse, so a caller draws no tile rather than `NaN`.
 */
export function eventDateTileParts(input) {
    const start = new Date(input.startsAt);
    if (Number.isNaN(start.getTime()))
        return null;
    const zone = input.timezone ?? 'UTC';
    const month = new Intl.DateTimeFormat('en-US', { timeZone: zone, month: 'short' }).format(start);
    const day = new Intl.DateTimeFormat('en-US', { timeZone: zone, day: 'numeric' }).format(start);
    // Title case on purpose; the tile uppercases it in CSS, so a screen reader says "Sep 23" rather
    // than spelling out S-E-P.
    return { month, day };
}
/**
 * The card's time line — `Thu · 8:30 AM` — in the event's own timezone, with the zone's
 * abbreviation where it differs from the reader's (`MEM-015` AC-2).
 *
 * The prototype's Discover card puts the weekday and the time beneath the name, because the tile
 * beside it already says the month and the day. This is that line; `formatEventDateTime` is the
 * full sentence for places with no tile.
 */
export function formatEventWeekdayTime(input) {
    const start = new Date(input.startsAt);
    if (Number.isNaN(start.getTime()))
        return '';
    const zone = input.timezone ?? 'UTC';
    const weekday = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' }).format(start);
    const abbreviation = timezoneAbbreviation(start, zone);
    const readerAbbreviation = timezoneAbbreviation(start, input.reader);
    const suffix = abbreviation !== null && abbreviation !== readerAbbreviation ? ` ${abbreviation}` : '';
    return `${weekday} · ${formatTime(start, zone)}${suffix}`;
}
/**
 * The short zone name at an instant — `EDT`, `PST`, `GMT+11`. `null` when the platform's ICU
 * cannot resolve the zone, in which case no label is better than a wrong one.
 */
export function timezoneAbbreviation(instant, zone) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            timeZoneName: 'short',
        }).formatToParts(instant);
        return parts.find((part) => part.type === 'timeZoneName')?.value ?? null;
    }
    catch {
        return null;
    }
}
/**
 * Initials for the gradient avatar the prototype puts beside every organizer name: the same as the
 * organizer's own page and tile (`ORG-031`, `organizationInitials`), and `?` for a blank name.
 */
export function initialsOfName(name) {
    return organizationInitials(name) || '?';
}
/**
 * The reader's own IANA zone, as the browser reports it.
 *
 * A function rather than a constant so a test can pass one in, and so a reader who changes their
 * device's zone mid-session is not told yesterday's answer.
 */
export function readerTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
//# sourceMappingURL=member-event-time.js.map